import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { useAccountGateBypass } from '@/hooks/useAccountGateBypass'
import {
  platformNoticeApi,
  type PlatformNotice,
  type PlatformNoticeLanguage,
  type PlatformNoticeTexts,
} from '@/services/api/platform-notice.api'

export const PLATFORM_NOTICE_QUERY_KEY = ['platform-notice'] as const

// Re-check every few minutes and on focus, so an admin switching the notice on
// or off reaches sessions that are already open without a reload.
const REFETCH_INTERVAL_MS = 5 * 60 * 1000

/**
 * #2072 — one shared read for the banner and the entry modal. Not asked while a
 * forced password change is pending: the API answers 403 until it is done, and
 * that modal comes first. An impersonating admin skips that gate (#1344).
 */
export function usePlatformNotice() {
  const { user, isAuthenticated } = useAuth()
  const { shouldBypassAccountGates } = useAccountGateBypass()
  const enabled = isAuthenticated && !!user && (!user.forcePasswordChange || shouldBypassAccountGates)

  return useQuery({
    queryKey: PLATFORM_NOTICE_QUERY_KEY,
    queryFn: platformNoticeApi.get,
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  })
}

/**
 * The text to show for one field: the admin's override for the current
 * interface language, or the default copy from the locale files.
 */
export function useNoticeText(notice: PlatformNotice | null | undefined) {
  const { t, i18n } = useTranslation('common')
  const lang: PlatformNoticeLanguage = (i18n.resolvedLanguage ?? i18n.language ?? 'en').startsWith('he') ? 'he' : 'en'
  return (field: keyof PlatformNoticeTexts): string =>
    notice?.texts?.[lang]?.[field] ?? t(`platformNotice.${field}`)
}
